/* eslint-disable no-nested-ternary */
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@type/index';
import { CONFIG_CALL_TO_ACTION_DEFAULT } from './constants';

import { initialState } from './slice';

const selectDomain = (state: RootState) => state.app || initialState;

export const selectApp = createSelector(
  [selectDomain],
  dashboardState => dashboardState,
);

export const selectIsLoading = createSelector(
  [selectDomain],
  state => state.loading,
);

export const selectIsError = createSelector(
  [selectDomain],
  state => state.error,
);

export const selectIsSuccess = createSelector(
  [selectDomain],
  state => state.success,
);

export const selectToken = createSelector([selectDomain], state => state.token);

/**
 * NOTE: DETAIL PROJECT SELECTORS
 */

export const selectDetailProject = createSelector(
  [selectDomain],
  state => state.project,
);

export const selectTitleProject = createSelector(
  [selectDetailProject],
  project => project?.title || undefined,
);

export const selectFirstSceneMediaProject = createSelector(
  [selectDetailProject],
  project => project?.first_scene?.media || undefined,
);

export const selectThumbnailProject = createSelector(
  [selectDetailProject],
  project => project?.thumbnail || undefined,
);

export const selectFeatureImageProject = createSelector(
  [selectDetailProject],
  project => project?.featured_image || undefined,
);

export const selectLanguageTranslateProject = createSelector(
  [selectDetailProject],
  project => project?.language || undefined,
);

/**
 * NOTE: CONFIG SETTING GLOBAL (DESIGN TAB) SELECTORS
 */

export const selectHotspotConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.hotspotConfig || undefined,
);

export const selectControlbarConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.controlbarConfig || undefined,
);

export const selectPolygonConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.polygonConfig || undefined,
);

export const selectThemeConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.themeConfig || undefined,
);

export const selectPopupConfigGlobal = createSelector(
  [selectDetailProject],
  project => project?.config?.design?.popupConfig || undefined,
);

export const selectCallToActionConfigGlobal = createSelector(
  [selectDetailProject],
  project => ({
    ...CONFIG_CALL_TO_ACTION_DEFAULT,
    ...project?.config?.design?.callToActionConfig,
  }),
);
/**
 * NOTE: CONFIG PROJECT SELECTORS (MARKETING / SETTING TAB)
 */

export const selectMetaDataProject = createSelector(
  [selectDetailProject],
  project => project?.config?.marketing?.meta_data || undefined,
);

export const selectGATrackingProject = createSelector(
  [selectDetailProject],
  project => project?.config?.marketing?.analytic?.uid || undefined,
);

export const selectGATagTrackingProject = createSelector(
  [selectDetailProject],
  project =>
    project?.config?.marketing?.analytic?.ggTag ||
    process.env.NEXT_PUBLIC_GA_TAG_DEFAULT ||
    undefined,
);

export const selectConfigLittlePlanetProject = createSelector(
  [selectDetailProject],
  project => project?.config?.setting?.intro_little_planet || undefined,
);

export const selectConfigFirstSceneProject = createSelector(
  [selectDetailProject],
  project => project?.config?.setting?.general?.first_scene || undefined,
);

export const selectLanguageProject = createSelector(
  [selectDetailProject],
  project => project?.config?.setting?.general?.language || 'en',
);

export const selectScriptChats = createSelector(
  [selectDetailProject],
  project => project?.config?.marketing?.script_chats || undefined,
);

/**
 * NOTE: CONFIG SELECTORS
 */

export const selectConfigAutoRotate = createSelector(
  [selectDomain],
  state => state.configAutoRotate || undefined,
);

export const selectConfigAutoChangeScene = createSelector(
  [selectDomain],
  state => state.configAutoChangeScene || undefined,
);

export const selectConfigSoundProject = createSelector(
  [selectDomain],
  state => state.configSoundProject || undefined,
);

export const selectIsFirstScene = createSelector(
  [selectDomain],
  state => state.isFirstScene,
);

export const selectLanguageSelected = createSelector(
  [selectDomain],
  state => state.languageSelected,
);

export const selectIsChangeLanguage = createSelector(
  [selectDomain],
  state => state.isChangeLanguage,
);

export const selectIsLanguageValid = createSelector(
  [selectDomain],
  state => state.isLanguageValid,
);

export const selectListMediaVideoPlay = createSelector(
  [selectDomain],
  state => state.listMediaVideoPlay,
);

export const selectListHotspotSoundPlay = createSelector(
  [selectDomain],
  state => state.listHotspotSoundPlay,
);

export const selectListHotspotInstructorPlay = createSelector(
  [selectDomain],
  state => state.listHotspotInstructorPlay,
);

/**
 * NOTE: DATA SELECTED/LIST SELECTORS
 */

export const selectListScene = createSelector(
  [selectDomain],
  state => state.listScene || [],
);

export const selectListGroup = createSelector([selectDomain], state =>
  state.listGroup.length === 1 && state.listGroup[0].id === 'uncategory'
    ? state.listGroup
    : state.listGroup.filter(item => item.id !== 'uncategory'),
);

export const selectListSceneByGroup = createSelector(
  [selectListGroup],
  listGroup =>
    listGroup
      .reduce((result, item) => {
        result.push(item.scenes);
        return result;
      }, [])
      .flat(),
);

export const selectSceneSelected = createSelector(
  [selectDomain],
  state => state.sceneSelected || undefined,
);

export const selectGroupSelected = createSelector(
  [selectListGroup, selectSceneSelected],
  (groups, scene) => groups.find(item => item.id === scene?.group) || undefined,
);

export const selectPrevSceneSelected = createSelector(
  [selectDomain],
  state => state.prevSceneSelected || undefined,
);

export const selectHotspotSelected = createSelector(
  [selectDomain],
  state => state.hotspotSelected || undefined,
);

export const selectHotspotSelectedOpenIframe = createSelector(
  [selectDomain],
  state => state.hotspotSelectedOpenIframe || null,
);

export const selectDetailFirstScene = createSelector(
  [selectDetailProject, selectListScene, selectListGroup],
  (project, listScene, listGroup) =>
    project?.config?.setting?.general?.first_scene
      ? listScene.filter(
          item => item.id === project?.config?.setting?.general?.first_scene,
        )[0] || listGroup[0].scenes[0]
      : listGroup.length
      ? listGroup[0].scenes[0]
      : undefined,
);

/**
 * NOTE: PLUGIN SELECTORS
 */

export const selectDataMap = createSelector(
  [selectDomain],
  state => state.dataMap || undefined,
);

export const selectConfigMap = createSelector(
  [selectDomain],
  state => state.configMap || undefined,
);

export const selectListTourRoute = createSelector(
  [selectDomain],
  state =>
    state.listTourRoute
      .filter(item => item.scenes.length)
      .map(route => ({
        ...route,
        scenes: route.scenes.map(_scene => {
          const getNextScene = currentScene => {
            const _index = route.scenes.findIndex(
              item => item.id === currentScene.id,
            );
            const scene =
              _index !== -1 && _index < route.scenes.length - 1
                ? route.scenes[_index + 1]
                : _index === route.scenes.length - 1
                ? route.scenes[0]
                : undefined;
            return scene;
          };
          const nextScene = getNextScene(_scene);
          const listHotspotPoint = _scene?.hotspots?.filter(
            item =>
              (item.type === 'point' || item.type === 'chevron') &&
              (item.config?.point?.target_scene_id === nextScene?.id ||
                item.config?.chevron?.target_scene_id === nextScene?.id),
          );
          const nextHotspot = listHotspotPoint.length
            ? listHotspotPoint[0]
            : undefined;

          return {
            ..._scene,
            ...{
              config: {
                ..._scene?.config,
                default_view: {
                  ..._scene?.config?.default_view,
                  ...{
                    x: nextHotspot?.ath || 0,
                    y: nextHotspot?.atv || 0,
                  },
                },
              },
            },
          };
        }),
      })) || [],
);

export const selectListFloorplan = createSelector(
  [selectDomain],
  state => state.listFloorplan || [],
);

export const selectConfigFloorplan = createSelector(
  [selectDomain],
  state => state.configFloorplan || undefined,
);

export const selectConfigLogo = createSelector(
  [selectDomain],
  state => state.configLogo || undefined,
);

export const selectConfigNadir = createSelector(
  [selectDomain],
  state => state.configNadir || undefined,
);

export const selectConfigProtectPassword = createSelector(
  [selectDomain],
  state => state.configProtectPassword || undefined,
);

export const selectConfigCopyRight = createSelector(
  [selectDomain],
  state => state.configCopyRight || undefined,
);

export const selectConfigPopupIntro = createSelector(
  [selectDomain],
  state => state.configPopupIntro || undefined,
);

export const selectListLanguage = createSelector(
  [selectDomain],
  state => state.listLanguage || [],
);

export const selectTourRouteSelected = createSelector(
  [selectDomain],
  state => state.tourRouteSelected,
);

export const selectPrevDestinationTourRoute = createSelector(
  [selectTourRouteSelected, selectSceneSelected],
  (tourRoute, sceneSelected) => {
    if (tourRoute) {
      const getPrevScene = currentScene => {
        const index = tourRoute.scenes.findIndex(
          item => item.id === currentScene.id,
        );
        const scene =
          index !== -1 && index > 0
            ? tourRoute.scenes[index - 1]
            : index === 0
            ? tourRoute.scenes[tourRoute.scenes.length - 1]
            : undefined;
        return scene;
      };
      const scene = getPrevScene(sceneSelected);
      return scene;
    }
    return undefined;
  },
);

export const selectNextDestinationTourRoute = createSelector(
  [selectTourRouteSelected, selectSceneSelected],
  (tourRoute, sceneSelected) => {
    if (tourRoute) {
      const getNextScene = currentScene => {
        const _index = tourRoute.scenes.findIndex(
          item => item.id === currentScene.id,
        );
        const scene =
          _index !== -1 && _index < tourRoute.scenes.length - 1
            ? tourRoute.scenes[_index + 1]
            : _index === tourRoute.scenes.length - 1
            ? tourRoute.scenes[0]
            : undefined;
        return scene;
      };
      const scene = getNextScene(sceneSelected);
      return scene;
    }
    return undefined;
  },
);

export const selectListCallToAction = createSelector(
  [selectDomain],
  state => state.listCallToAction || [],
);
